/**
 * Badges — gefüllte Pills in Semantikfarbe mit Ink-Outline.
 *
 * Alle drei Badges teilen sich absichtlich EINE Pill-Grundform: Sie stehen im
 * Spiel oft nebeneinander (Typ · Tier · Rarität) und müssten sonst mühsam
 * aufeinander abgestimmt gehalten werden.
 *
 * ⚠️ Textfarbe: Auf hellen Füllungen (neon, glitch, gold, pink) steht Ink, auf
 * dunklen (void, plasma, data) Weiß. Wer eine neue Farbe ergänzt, trägt sie in
 * `DARK_TEXT` ein — sonst steht dunkler Text auf dunklem Grund.
 *
 * Die fünf Typfarben sind semantisch und werden nie dekorativ benutzt.
 *
 * Spezifikation: docs/design-system/components/game/{TypeBadge,TierBadge,RarityTag}.jsx
 */
import type { ReactNode } from "react";
import { Crown, Skull, Zap } from "lucide-react";
import { InfoTip } from "@/components/game/InfoTip";
import { cn } from "@/lib/utils";
import { dropChanceFor } from "@/lib/game/battle-v2/loot-item";
import {
  type CreatureType,
} from "@/lib/game/types";

/** Die gemeinsame Pill. `style` trägt die Füllfarbe, weil sie aus Tokens kommt. */
export function Pill({
  className,
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border-2 border-ink px-2 py-px",
        "font-display text-[10px] uppercase leading-relaxed tracking-[0.12em] [text-shadow:none]",
        className,
      )}
      style={style}
    >
      {children}
    </span>
  );
}

export const TYPE_LABELS: Record<CreatureType, string> = {
  neon: "Neon",
  void: "Void",
  plasma: "Plasma",
  data: "Data",
  glitch: "Glitch",
};

/** Helle Füllungen brauchen Ink-Text, sonst ist die Pill unlesbar. */
const TYPE_TEXT_INK: Record<CreatureType, boolean> = {
  neon: true,
  glitch: true,
  plasma: false,
  void: false,
  data: false,
};

/**
 * Reines Typ-Abzeichen.
 *
 * ⚠️ Hier stand bis zum 09.09.2026 zusätzlich eine Vorteils-Pille („Dein Typ
 * ist stark gegen diesen Gegner"), die den Faktor als Zahl auswies. Sie ist
 * ersatzlos entfallen: Seit
 * dem 07.09.2026 entscheidet der Typ der ATTACKE über den Vorteil, nicht der
 * des Kämpfers — und ein Xenomon trägt Attacken verschiedener Typen. Eine
 * Pille am Kämpfer kann die Frage deshalb gar nicht mehr beantworten.
 * Zuständig ist jetzt die einzelne Slot-Kachel (`MoveTile`), die ihren Punkt
 * aus `typVorteilFuerAnzeige` bezieht.
 */
export function TypeBadge({
  type,
  label,
  className,
}: {
  type: CreatureType;
  label?: string;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Pill
        style={{
          background: `var(--type-${type})`,
          color: TYPE_TEXT_INK[type] ? "var(--ink)" : "var(--foreground)",
        }}
      >
        {label ?? TYPE_LABELS[type] ?? type}
      </Pill>
    </span>
  );
}

export type EnemyTier = "normal" | "elite" | "boss";

/** Anteil als deutsche Prozentangabe („0.45" → „45 %"). */
function pct(anteil: number): string {
  return `${Math.round(anteil * 100)} %`;
}

/**
 * Die Erklärtexte der Tier-Pille.
 *
 * ⚠️ Jede Zahl hier kommt aus `dropChanceFor` — derselben Funktion, die
 * `rollLootItem` würfelt. Nicht durch eine getippte Zahl ersetzen: Die Pille
 * behauptete bis 05.09.2026 bei Elite „mehr XP und Gold" (beides hängt an
 * `xpPerKill(enemyLevel)` und `kills * 5` in `combat-core/simulate.ts`, also am
 * Level des Gegners, nicht an seinem Rang) und beim Boss ein garantiertes Ei —
 * das gibt es laut `eggChanceFor` erst ab Zone 2, und ausgerechnet der erste
 * Boss, den ein Spieler sieht (Zenithra in Zone 1), lässt keines fallen.
 *
 * 📌 Die Ei-Regel steht bewusst OHNE Zonen-Zahl da: `eggChanceFor` liegt in
 * `settle.functions.ts` (Server-Modul, hier nicht importierbar). Die Regel hier
 * nachzubauen hieße, sie zweimal zu pflegen. Sobald sie als reines Modul
 * vorliegt, kann die Pille die Zone kennen und „garantiert" bzw. „35 %" nennen.
 */
const TIERS: Record<
  EnemyTier,
  { label: string; bg: string; fg: string; icon: typeof Skull; tip: string }
> = {
  normal: {
    label: "Gegner",
    bg: "var(--secondary)",
    fg: "var(--foreground)",
    icon: Skull,
    tip: `Normaler Gegner. Ausrüstung fällt mit ${pct(dropChanceFor("normal"))}.`,
  },
  elite: {
    label: "Elite",
    bg: "var(--elite)",
    fg: "var(--ink)",
    icon: Zap,
    tip:
      `Elite-Gegner: härter. Ausrüstung fällt mit ${pct(dropChanceFor("elite"))} ` +
      `statt ${pct(dropChanceFor("normal"))}. XP und Gold steigen dadurch nicht: ` +
      `XP hängt am Level des Gegners, Gold ist pro besiegtem Gegner gleich.`,
  },
  boss: {
    label: "Boss",
    bg: "var(--boss)",
    fg: "var(--ink)",
    icon: Crown,
    tip:
      `Boss: viel HP und Schaden. Bei der Erstbegehung fällt Ausrüstung mit ` +
      `${pct(dropChanceFor("boss"))}, ab Zone 2 auch ein Ei. Im Training fällt ` +
      `keine Ausrüstung.`,
  },
};

export function TierBadge({ tier, className }: { tier: EnemyTier; className?: string }) {
  const t = TIERS[tier] ?? TIERS.normal;
  const Icon = t.icon;
  return (
    <InfoTip text={t.tip}>
      <Pill className={className} style={{ background: t.bg, color: t.fg }}>
        <Icon className="h-3 w-3" strokeWidth={2.5} />
        {t.label}
      </Pill>
    </InfoTip>
  );
}

export type Rarity = "common" | "rare" | "epic" | "legendary";

const RARITY_LABELS: Record<Rarity, string> = {
  common: "Gewöhnlich",
  rare: "Selten",
  epic: "Episch",
  legendary: "Legendär",
};

export function RarityTag({
  rarity,
  label,
  className,
}: {
  rarity: Rarity;
  label?: string;
  className?: string;
}) {
  return (
    <Pill
      className={className}
      style={{ background: `var(--rarity-${rarity})`, color: "var(--ink)" }}
    >
      {label ?? RARITY_LABELS[rarity] ?? rarity}
    </Pill>
  );
}
